import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Backspace, Check, X } from '@phosphor-icons/react';
import { useAuth } from '../App';

/**
 * Reusable numeric keypad popover. Used to set an exact item quantity
 * without spamming the +/- buttons.
 *
 * Props:
 *   open       — boolean: dialog visibility
 *   initial    — number: starting value (typically the current qty)
 *   max        — number | null: optional cap (e.g. stock available). The user
 *                can type higher but the Set button is disabled above max.
 *   title      — string: e.g. "Bier × ?"
 *   subtitle   — optional string under the title (e.g. "auf Lager: 12")
 *   onConfirm  — (n: number) => void
 *   onClose    — () => void
 */
const NumPad = ({ open, initial = 0, max = null, title, subtitle, onConfirm, onClose }) => {
  const { language } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const [value, setValue] = useState(String(initial || 0));

  useEffect(() => {
    if (open) setValue(String(initial || 0));
  }, [open, initial]);

  // Hardware keyboard support (laptop / external keyboard on iPad)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        appendDigit(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        backspace();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        confirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  const appendDigit = (d) => {
    setValue((v) => {
      // Replace leading zero, cap at 4 digits (9999 max)
      const next = v === '0' ? d : (v + d).slice(0, 4);
      return next;
    });
  };
  const backspace = () => setValue((v) => (v.length <= 1 ? '0' : v.slice(0, -1)));
  const clear = () => setValue('0');
  const confirm = () => {
    const n = parseInt(value || '0', 10);
    if (!Number.isFinite(n) || n < 0) return;
    if (max != null && n > max) return;
    onConfirm?.(n);
  };

  const numeric = parseInt(value || '0', 10);
  const overMax = max != null && numeric > max;

  const KEYS = [['1','2','3'],['4','5','6'],['7','8','9'],['C','0','⌫']];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-xs sm:max-w-sm" data-testid="numpad-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold leading-tight">{title || L('Set quantity', 'Menge setzen')}</DialogTitle>
          {subtitle && <DialogDescription className="text-xs text-slate-500">{subtitle}</DialogDescription>}
        </DialogHeader>

        {/* Display */}
        <div className={`select-none my-1 px-4 py-4 rounded-xl text-center text-4xl font-extrabold tabular-nums tracking-wider ${
          overMax ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
        }`} data-testid="numpad-display">
          {numeric}
        </div>
        {overMax && (
          <p className="text-[11px] text-red-600 -mt-1 text-center font-semibold">
            {L(`Max ${max}`, `Max. ${max}`)}
          </p>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-2 select-none">
          {KEYS.flat().map((k) => {
            const isClear = k === 'C';
            const isBack = k === '⌫';
            const handler = isClear ? clear : isBack ? backspace : () => appendDigit(k);
            return (
              <button
                key={k}
                onClick={handler}
                className={`h-14 rounded-xl text-xl font-bold transition-all active:scale-95 ${
                  isClear ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' :
                  isBack  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' :
                  'bg-white border border-slate-200 text-slate-800 hover:bg-emerald-50 hover:border-emerald-300'
                }`}
                data-testid={`numpad-key-${k === '⌫' ? 'back' : k === 'C' ? 'clear' : k}`}
              >
                {isBack ? <Backspace size={20} weight="bold" className="mx-auto" /> : k}
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 border-slate-200 text-slate-600" data-testid="numpad-cancel">
            <X size={16} className="mr-1" /> {L('Cancel', 'Abbrechen')}
          </Button>
          <Button
            onClick={confirm}
            disabled={overMax || numeric < 0}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold disabled:bg-slate-300"
            data-testid="numpad-confirm"
          >
            <Check size={16} className="mr-1" /> {L(`Set ${numeric}`, `${numeric} setzen`)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NumPad;
